import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users, Plus, Heart, MessageSquare, BookOpen, Crown
} from "lucide-react";

export function DiscussionBoardZone({
  discussionPosts,
  discussionCategory,
  setDiscussionCategory,
  fetchDiscussionPosts,
  membersDirectory,
  handleLikePost,
  handleViewComments,
  openProfileCard,
  setShowPostDiscussion
}) {
  return (
    <div className="space-y-6">
      {/* How-to Instructions Card */}
      <Card className="bg-gradient-to-r from-blue-500/10 to-neutral-900/50 border-blue-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">How to Use the Discussion Board</h3>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">1.</span>
                  <span><strong>Post questions or insights</strong> - Share your wins, challenges, or advice with fellow members</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">2.</span>
                  <span><strong>Filter by category</strong> - Find discussions relevant to your needs (General, Questions, Wins, etc.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">3.</span>
                  <span><strong>Engage with comments</strong> - Support others by commenting, earn community points for participation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-bold">4.</span>
                  <span><strong>Browse the member directory</strong> - Discover potential partners and collaborators</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Feed */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#39FF14]" />
              Discussion Board
            </h2>
            <div className="flex gap-2">
              <Select value={discussionCategory} onValueChange={(v) => { setDiscussionCategory(v); fetchDiscussionPosts(v); }}>
                <SelectTrigger className="w-[140px] bg-neutral-800 border-neutral-700">
                  <SelectValue placeholder="All Posts" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-800 border-neutral-700">
                  <SelectItem value="all">All Posts</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="question">Questions</SelectItem>
                  <SelectItem value="win">Wins & Celebrations</SelectItem>
                  <SelectItem value="intro">Introductions</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={() => setShowPostDiscussion(true)}
                className="bg-[#39FF14] text-black hover:bg-[#32e612]"
                data-testid="create-post-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Post</span>
              </Button>
            </div>
          </div>

          {/* Posts */}
          {discussionPosts.length > 0 ? discussionPosts.map((post) => (
            <Card key={post.post_id} className="bg-neutral-800/50 border-neutral-700">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    {post.author_picture ? (
                      <img src={post.author_picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <Users className="w-5 h-5 text-[#39FF14]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-white cursor-pointer hover:text-[#39FF14] transition-colors" onClick={() => openProfileCard(post.user_id)} data-testid={`profile-link-${post.post_id}`}>{post.author_name}</span>
                      <Badge variant="outline" className={`text-xs ${
                        post.category === "win" ? "border-amber-500/30 text-amber-400" :
                        post.category === "question" ? "border-blue-500/30 text-blue-400" :
                        post.category === "intro" ? "border-purple-500/30 text-purple-400" :
                        "border-neutral-600 text-neutral-400"
                      }`}>
                        {post.category === "win" ? "Win" :
                         post.category === "question" ? "Question" :
                         post.category === "intro" ? "Intro" : "General"}
                      </Badge>
                      <span className="text-xs text-neutral-500">
                        {post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <p className="text-neutral-300 whitespace-pre-wrap">{post.content}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLikePost(post.post_id)}
                        className={`${post.liked_by_me ? "text-red-400" : "text-neutral-400"} hover:text-red-400`}
                      >
                        <Heart className={`w-4 h-4 mr-1 ${post.liked_by_me ? "fill-current" : ""}`} />
                        {post.likes_count || 0}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewComments(post)}
                        className="text-neutral-400 hover:text-white"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {post.comments_count || 0}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="bg-neutral-800/50 border-neutral-700">
              <CardContent className="text-center py-12">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
                <p className="text-neutral-400">No posts yet</p>
                <p className="text-sm text-neutral-500 mt-1">Be the first to share something!</p>
                <Button
                  onClick={() => setShowPostDiscussion(true)}
                  className="mt-4 bg-[#39FF14] text-black hover:bg-[#32e612]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Post
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Members Directory Sidebar */}
        <div className="space-y-4">
          <Card className="bg-neutral-800/50 border-neutral-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#39FF14]" />
                Community Members
              </CardTitle>
              <CardDescription className="text-neutral-400">
                {membersDirectory.length} members
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {membersDirectory.slice(0, 15).map((member, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-700/50 transition-colors cursor-pointer" onClick={() => openProfileCard(member.user_id)}>
                    <div className="w-8 h-8 bg-[#39FF14]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      {member.picture ? (
                        <img src={member.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <Users className="w-4 h-4 text-[#39FF14]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate hover:text-[#39FF14]">{member.name || "Member"}</p>
                      <p className="text-xs text-neutral-500 truncate">{member.business_description?.slice(0, 40) || "Community Member"}</p>
                    </div>
                    {member.membership_tier === "premium" && (
                      <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="bg-neutral-800/50 border-neutral-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg">Community Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-neutral-900 rounded-lg">
                  <p className="text-2xl font-bold text-[#39FF14]">{membersDirectory.length}</p>
                  <p className="text-xs text-neutral-400">Members</p>
                </div>
                <div className="text-center p-3 bg-neutral-900 rounded-lg">
                  <p className="text-2xl font-bold text-[#39FF14]">{discussionPosts.length}</p>
                  <p className="text-xs text-neutral-400">Posts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
